import React, { useEffect, useState } from 'react';
import { CustomField, CustomFieldUtil } from '../../../../../types/custom_field';
import { CustomFieldFilter } from '../CustomFieldFilter';
import { FilterProps } from '../../common/filter.util';
import { Divider, Grid } from '@mui/material';
import { DeleteIcon } from '../../../../icons/Delete/DeleteIcon';
import InnerFilterContent from '../../common/DropdownContent/DynamicFilterInnerContent';
import { VentoryColor } from '../../../../util/color.util';
import MultiSelectionList from '../../../common/MultiSelectionList';
import SearchBar from '../../../common/SearchBar';
import { asTranslatedString } from '../../../../../types/translation/Translator';
import FilterDropdown from '../../common/FilterDropdown';
import { Icon } from '../../../../../ui/icons';
import { IconType } from '../../../../../ui/icons/Icon';

interface CustomFieldFilterListOfValuesProps {
  customField: CustomField;
  filter: CustomFieldFilter;
  filterProps: FilterProps;
  setFilterProps: (filter: FilterProps) => void;
}

export const CustomFieldFilterListOfValuesContent = ({
  customField,
  filter,
  filterProps,
  setFilterProps,
}: CustomFieldFilterListOfValuesProps) => {
  const [query, setQuery] = useState<string>('');

  const filterValues = () => {
    if (!query) return new Set(customField.values);
    return new Set(customField.values.filter(value => value.toLowerCase().includes(query.toLowerCase())));
  };

  const [values, setValues] = useState<Set<string>>(filterValues());

  const handleChange = (selected: Set<string>, item: string) => {
    if (!filterProps.customFieldValues) filterProps.customFieldValues = new Map();
    if (selected.size) {
      filterProps.customFieldValues.set(customField.id, new Set(selected));
    } else {
      filterProps.customFieldValues.delete(customField.id);
    }
    setFilterProps({ ...filterProps });
  };

  const handleRemove = () => {
    filterProps.customFieldValues?.delete(customField.id);
    setFilterProps({ ...filterProps });
  };

  useEffect(() => {
    setValues(filterValues());
  }, [query]);

  const label = asTranslatedString(customField.name);

  const innerContent = (
    <InnerFilterContent
      filterProps={{ filterProps, setFilterProps }}
      filter={filter}
      items={[...(filterProps.customFieldValues?.get(customField.id)?.values() || [])]}
      text={item => item.charAt(0).toLocaleUpperCase() + item.slice(1)}
      onRemove={i => {
        filterProps.customFieldValues?.get(customField.id)?.delete(i);
        if (!filterProps.customFieldValues?.get(customField.id)?.size) {
          filterProps.customFieldValues?.delete(customField.id);
        }
        setFilterProps({ ...filterProps });
      }}
    />
  );

  const dropdownContent = (
    <Grid container>
      <Grid item xs={12} className='flex px-3 pt-2'>
        <Grid container>
          <Grid item>
            <p className='text-ventory-grey-500 text-[13px] font-[500]'>{label}</p>
          </Grid>
          <Grid item flexGrow={1} display={'flex'} justifyContent={'flex-end'} sx={{ color: VentoryColor.deleteRed }}>
            <Grid item onClick={handleRemove} className='cursor-pointer'>
              <DeleteIcon />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <SearchBar value={query} onChange={i => setQuery(i)} className='pl-3 pr-4' />
      </Grid>
      <Grid item className='py-3' xs={12}>
        <Divider />
      </Grid>
      <Grid item xs={12}>
        <MultiSelectionList
          selected={new Set([...(filterProps.customFieldValues?.get(customField.id)?.values() || [])])}
          values={values}
          onChange={handleChange}
          toText={id => id}
        />
      </Grid>
    </Grid>
  );

  return (
    <>
      <p className='mb-1 text-sm text-gray-900'>{`${label} (${CustomFieldUtil.customFieldEntityTypeToString(
        customField.entityType,
      )})`}</p>
      <FilterDropdown
        icon={<Icon type={IconType.dataFlow4} size={'size-4'} />}
        innerContent={innerContent}
        dropdownContent={dropdownContent}
        badge={filter.toCount({ filterProps, setFilterProps })}
      />
    </>
  );
};
